import React, { useMemo } from 'react';
import { Dialog } from 'primereact/dialog';
import { Link } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';

const MS_PER_DAY = 86400000;

const startOfLocalDay = (d) => {
    const x = new Date(d);
    return new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
};

const licenseYearsFromInfo = (info) => {
    if (info?.duration_days != null && Number(info.duration_days) > 0) {
        const y = Math.round(Number(info.duration_days) / 365);
        return Math.max(1, y);
    }
    if (info?.activation_license_date && info?.expiration_license_date) {
        const ms = new Date(info.expiration_license_date) - new Date(info.activation_license_date);
        const days = ms / MS_PER_DAY;
        if (Number.isFinite(days) && days > 0) return Math.max(1, Math.round(days / 365));
    }
    return 1;
};

export const ModalLicenseExpiration = ({ isOpen, onClose, fieldId, isOwner, licenseInfo }) => {
    const { t, i18n } = useTranslation();

    const localeTag = (i18n.language || 'es').replace('_', '-');

    const formatDdMmYyyy = (iso) => {
        if (!iso) return '\u2014';
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return '\u2014';
        return d.toLocaleDateString(localeTag, { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    const { dialogHeader, dialogBody } = useMemo(() => {
        const expRaw = licenseInfo?.expiration_license_date;
        if (!expRaw) {
            if (isOwner) {
                return {
                    dialogHeader: t('field_card.no_available'),
                    dialogBody: (
                        <span>
                            {t('license_expiration.not_license_owner')}{' '}
                            <Link to={`/linkSensor/${fieldId}`} state={{ fieldId }} className="text-primary">
                                {t('field_card.link_sensor_1_click')}
                            </Link>
                        </span>
                    )
                };
            }
            return {
                dialogHeader: t('field_card.no_available'),
                dialogBody: <span>{t('license_expiration.not_license_shared')}</span>
            };
        }

        const activation = formatDdMmYyyy(licenseInfo?.activation_license_date);
        const expiration = formatDdMmYyyy(expRaw);
        const years = licenseYearsFromInfo(licenseInfo);
        const params = { years, activation, expiration };

        const expDay = startOfLocalDay(expRaw);
        const todayDay = startOfLocalDay(new Date());
        const expired = todayDay > expDay;

        if (expired) {
            return {
                dialogHeader: t('license_expiration.title_expired'),
                dialogBody: (
                    <span>
                        <Trans
                            i18nKey="license_expiration.body_expired"
                            values={params}
                            components={{ strong: <strong /> }}
                        />
                    </span>
                )
            };
        }
        return {
            dialogHeader: t('license_expiration.title_warning'),
            dialogBody: (
                <span>
                    <Trans
                        i18nKey="license_expiration.body_warning"
                        values={params}
                        components={{ strong: <strong /> }}
                    />
                </span>
            )
        };
    }, [licenseInfo, fieldId, isOwner, t, i18n.language]);

    return (
        <Dialog
            header={dialogHeader}
            visible={isOpen}
            focusOnShow={false}
            className="w-11 desktop-max-width border-round-2xl"
            onHide={onClose}
            onClick={(e) => e.stopPropagation()}
            pt={{
                header: { className: 'border-round-top-2xl' },
                content: { className: 'border-round-bottom-2xl' }
            }}
            modal
        >
            <div>
                <p className="m-0">{dialogBody}</p>
            </div>
        </Dialog>
    );
};
